import { getVideoContext } from './videoContextService';
import { getChannelContext, incrementChannelIntent, incrementChannelFriction, addLearnedPattern } from './channelContextService';
import { getSignalsForVideo } from './commentSignalsService';

/**
 * Promotion Service
 * Handles auto-promotion of patterns from video ÔåÆ channel context
 */

const PROMOTION_THRESHOLDS = {
  MIN_VIDEO_COUNT: 3,           // Minimum videos to consider for promotion
  MIN_INTENT_FREQUENCY: 3,      // Intent must appear in 3+ videos
  MIN_FRICTION_FREQUENCY: 2,    // Friction must appear in 2+ videos
  MIN_QUESTION_FREQUENCY: 2,    // Question must appear in 2+ videos
  MIN_CONFIDENCE: 0.7           // Minimum average confidence for promotion
};

/**
 * Check if an intent should be promoted to channel context
 * @param {string} intent - Intent type
 * @param {Array} videoContexts - Array of video context objects
 * @returns {boolean} Whether to promote
 */
function shouldPromoteIntent(intent, videoContexts) {
  // Count how many videos have this intent with sufficient confidence
  let videoCount = 0;
  let totalConfidence = 0;
  let occurrenceCount = 0;

  videoContexts.forEach(context => {
    if (context.topIntents && context.topIntents[intent]) {
      const intentCount = context.topIntents[intent];
      if (intentCount > 0 && context.confidenceScore >= PROMOTION_THRESHOLDS.MIN_CONFIDENCE) {
        videoCount++;
        totalConfidence += context.confidenceScore;
        occurrenceCount += intentCount;
      }
    }
  });

  const avgConfidence = videoCount > 0 ? totalConfidence / videoCount : 0;

  return (
    videoCount >= PROMOTION_THRESHOLDS.MIN_INTENT_FREQUENCY &&
    avgConfidence >= PROMOTION_THRESHOLDS.MIN_CONFIDENCE
  );
}

/**
 * Check if a friction theme should be promoted to channel context
 * @param {string} theme - Friction theme
 * @param {Array} videoContexts - Array of video context objects
 * @returns {boolean} Whether to promote
 */
function shouldPromoteFriction(theme, videoContexts) {
  let videoCount = 0;
  let totalConfidence = 0;

  videoContexts.forEach(context => {
    if (context.topFrictionThemes && context.topFrictionThemes[theme]) {
      const themeCount = context.topFrictionThemes[theme];
      if (themeCount > 0 && context.confidenceScore >= PROMOTION_THRESHOLDS.MIN_CONFIDENCE) {
        videoCount++;
        totalConfidence += context.confidenceScore;
      }
    }
  });

  const avgConfidence = videoCount > 0 ? totalConfidence / videoCount : 0;

  return (
    videoCount >= PROMOTION_THRESHOLDS.MIN_FRICTION_FREQUENCY &&
    avgConfidence >= PROMOTION_THRESHOLDS.MIN_CONFIDENCE
  );
}

/**
 * Check if a question topic should be promoted to channel context
 * @param {string} topic - Question topic
 * @param {Array} videoContexts - Array of video context objects
 * @returns {boolean} Whether to promote
 */
function shouldPromoteQuestion(topic, videoContexts) {
  let videoCount = 0;
  let totalOccurrences = 0;

  videoContexts.forEach(context => {
    if (context.unresolvedQuestions && context.unresolvedQuestions[topic]) {
      const topicCount = context.unresolvedQuestions[topic];
      if (topicCount > 0) {
        videoCount++;
        totalOccurrences += topicCount;
      }
    }
  });

  return videoCount >= PROMOTION_THRESHOLDS.MIN_QUESTION_FREQUENCY;
}

/**
 * Promote intents from video contexts to channel context
 * @param {string} channelId - Channel ID
 * @param {Array} videoContexts - Array of video context objects
 */
async function promoteIntents(channelId, videoContexts) {
  // Collect all unique intents
  const allIntents = new Set();
  videoContexts.forEach(context => {
    if (context.topIntents) {
      Object.keys(context.topIntents).forEach(intent => allIntents.add(intent));
    }
  });

  const promotedIntents = [];

  // Check each intent for promotion
  for (const intent of allIntents) {
    if (shouldPromoteIntent(intent, videoContexts)) {
      // Calculate total occurrences for this intent
      const totalOccurrences = videoContexts.reduce((sum, context) => {
        return sum + (context.topIntents?.[intent] || 0);
      }, 0);

      // Promote to channel (increment by total occurrences)
      for (let i = 0; i < totalOccurrences; i++) {
        await incrementChannelIntent(channelId, intent);
      }

      promotedIntents.push({ intent, occurrences: totalOccurrences });
      console.log(`Ô£à [Promotion] Promoted intent '${intent}' to channel: ${channelId}`);
    }
  }

  return promotedIntents;
}

/**
 * Promote friction themes from video contexts to channel context
 * @param {string} channelId - Channel ID
 * @param {Array} videoContexts - Array of video context objects
 */
async function promoteFrictionThemes(channelId, videoContexts) {
  // Collect all unique friction themes
  const allThemes = new Set();
  videoContexts.forEach(context => {
    if (context.topFrictionThemes) {
      Object.keys(context.topFrictionThemes).forEach(theme => allThemes.add(theme));
    }
  });

  const promotedThemes = [];

  // Check each theme for promotion
  for (const theme of allThemes) {
    if (shouldPromoteFriction(theme, videoContexts)) {
      // Calculate total occurrences for this theme
      const totalOccurrences = videoContexts.reduce((sum, context) => {
        return sum + (context.topFrictionThemes?.[theme] || 0);
      }, 0);

      // Promote to channel
      for (let i = 0; i < totalOccurrences; i++) {
        await incrementChannelFriction(channelId, theme);
      }

      promotedThemes.push({ theme, occurrences: totalOccurrences });
      console.log(`Ô£à [Promotion] Promoted friction theme '${theme}' to channel: ${channelId}`);
    }
  }

  return promotedThemes;
}

/**
 * Generate learned patterns from video contexts
 * @param {Array} videoContexts - Array of video context objects
 * @param {Array} promotedIntents - Promoted intents
 * @param {Array} promotedThemes - Promoted friction themes
 * @returns {Array} Array of pattern descriptions
 */
function generateLearnedPatterns(videoContexts, promotedIntents, promotedThemes) {
  const patterns = [];

  // Generate intent patterns
  promotedIntents.forEach(({ intent, occurrences }) => {
    const videoCount = videoContexts.filter(c => c.topIntents?.[intent] > 0).length;
    patterns.push(
      `Recurring ${intent} intent observed across ${videoCount} videos (${occurrences} total occurrences)`
    );
  });

  // Generate friction patterns
  promotedThemes.forEach(({ theme, occurrences }) => {
    const videoCount = videoContexts.filter(c => c.topFrictionThemes?.[theme] > 0).length;
    patterns.push(
      `Persistent friction around "${theme}" in ${videoCount} videos (${occurrences} mentions)`
    );
  });

  // Analyze dominant audience level
  const audienceLevels = videoContexts
    .map(c => c.dominantAudienceLevel)
    .filter(Boolean);
  
  if (audienceLevels.length > 0) {
    const levelCounts = {};
    audienceLevels.forEach(level => {
      levelCounts[level] = (levelCounts[level] || 0) + 1;
    });
    const dominantLevel = Object.keys(levelCounts).reduce((a, b) =>
      levelCounts[a] > levelCounts[b] ? a : b
    );
    patterns.push(`Audience typically at ${dominantLevel} level`);
  }

  return patterns;
}

/**
 * Run promotion process for a channel
 * @param {string} channelId - Channel ID
 * @param {Array} videoIds - Array of video IDs to analyze
 */
export async function runPromotionProcess(channelId, videoIds) {
  try {
    console.log(`­ƒöä [Promotion] Starting promotion process for channel: ${channelId}`);

    // Get all video contexts
    const videoContexts = await Promise.all(
      videoIds.map(videoId => getVideoContext(videoId))
    );

    // Filter out videos without sufficient data
    const validContexts = videoContexts.filter(
      context => context.confidenceScore >= PROMOTION_THRESHOLDS.MIN_CONFIDENCE
    );

    if (validContexts.length < PROMOTION_THRESHOLDS.MIN_VIDEO_COUNT) {
      console.log(`ÔÜá´©Å [Promotion] Insufficient video data for promotion (${validContexts.length}/${PROMOTION_THRESHOLDS.MIN_VIDEO_COUNT})`);
      return {
        promoted: false,
        reason: 'Insufficient video data'
      };
    }

    // Promote intents
    const promotedIntents = await promoteIntents(channelId, validContexts);

    // Promote friction themes
    const promotedThemes = await promoteFrictionThemes(channelId, validContexts);

    // Generate and save learned patterns
    if (promotedIntents.length > 0 || promotedThemes.length > 0) {
      const patterns = generateLearnedPatterns(validContexts, promotedIntents, promotedThemes);
      
      for (const pattern of patterns) {
        await addLearnedPattern(channelId, pattern);
      }
    }

    console.log(`Ô£à [Promotion] Promotion complete for channel: ${channelId}`);
    console.log(`   - Promoted ${promotedIntents.length} intents`);
    console.log(`   - Promoted ${promotedThemes.length} friction themes`);

    return {
      promoted: true,
      promotedIntents,
      promotedThemes,
      videoCount: validContexts.length
    };
  } catch (error) {
    console.error('ÔØî [Promotion] Error running promotion:', error);
    throw error;
  }
}

/**
 * Check if promotion should run for a channel
 * @param {string} channelId - Channel ID
 * @param {Array} videoIds - Array of video IDs
 * @returns {boolean} Whether to run promotion
 */
export async function shouldRunPromotion(channelId, videoIds) {
  try {
    const channelContext = await getChannelContext(channelId);
    const videoContexts = await Promise.all(
      videoIds.map(videoId => getVideoContext(videoId))
    );

    const validContexts = videoContexts.filter(
      context => context.confidenceScore >= PROMOTION_THRESHOLDS.MIN_CONFIDENCE
    );

    // Check if we have enough videos
    if (validContexts.length < PROMOTION_THRESHOLDS.MIN_VIDEO_COUNT) {
      return false;
    }

    // Check if channel context was recently updated
    const lastUpdate = channelContext.lastUpdated;
    const now = new Date();
    const daysSinceUpdate = (now - lastUpdate) / (1000 * 60 * 60 * 24);

    // Run promotion if:
    // 1. Never promoted (no learned patterns)
    // 2. More than 7 days since last update and new videos exist
    const shouldRun = 
      !channelContext.learnedPatterns ||
      channelContext.learnedPatterns.length === 0 ||
      daysSinceUpdate > 7;

    return shouldRun;
  } catch (error) {
    console.error('ÔØî [Promotion] Error checking promotion status:', error);
    return false;
  }
}











